import React, { useState, useEffect } from 'react';
import { API_BASE } from '../config/api';
import { getSyrveClient, initializeSyrve } from '../services/syrveClient';
import {
  syncUserWithSyrve,
  getSyrveBalance,
  syncLoyaltyCard,
  getOrCreateSyrveCustomer
} from '../services/syrveLoyaltySync';
import { authentication } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import '../css/SyrveTestPage.css';

const SyrveTestPage = () => {
  const [user, setUser] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('Not connected');
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [balance, setBalance] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [testResults, setTestResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(authentication, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Test 1: Connect to Syrve API
  const testConnection = async () => {
    setLoading(true);
    try {
      const client = await initializeSyrve();
      setConnectionStatus('Connected');
      addTestResult('API Connection', 'Success', 'Connected to Syrve API');

      // Get organizations
      const orgsResponse = await client.getOrganizations();
      if (orgsResponse && orgsResponse.organizations) {
        setOrganizations(orgsResponse.organizations);
        if (orgsResponse.organizations.length > 0) {
          setSelectedOrg(orgsResponse.organizations[0]);
          client.setOrganization(orgsResponse.organizations[0].id);
          addTestResult('Organizations', 'Success', `Found ${orgsResponse.organizations.length} organizations`);
        }
      }
    } catch (error) {
      setConnectionStatus('Failed');
      addTestResult('API Connection', 'Failed', error.message);
    } finally {
      setLoading(false);
    }
  };

  // Test 2: Sync current user with Syrve
  const testUserSync = async () => {
    if (!user) {
      addTestResult('User Sync', 'Failed', 'No user logged in');
      return;
    }

    setLoading(true);
    try {
      const customer = await getOrCreateSyrveCustomer(user.email);
      setCustomerInfo(customer);
      addTestResult('User Sync', 'Success', `Customer ID: ${customer.id}`);
    } catch (error) {
      addTestResult('User Sync', 'Failed', error.message);
    } finally {
      setLoading(false);
    }
  };

  // Test 3: Get loyalty programs
  const testGetPrograms = async () => {
    setLoading(true);
    try {
      const client = getSyrveClient();
      const programsResponse = await client.getLoyaltyPrograms();
      setPrograms(programsResponse.programs || []);
      addTestResult('Loyalty Programs', 'Success', `Found ${programsResponse.programs?.length || 0} programs`);
    } catch (error) {
      addTestResult('Loyalty Programs', 'Failed', error.message);
    } finally {
      setLoading(false);
    }
  };

  // Test 4: Get wallet balance
  const testGetBalance = async () => {
    if (!user) {
      addTestResult('Get Balance', 'Failed', 'No user logged in');
      return;
    }

    setLoading(true);
    try {
      const balanceInfo = await getSyrveBalance(user.email);
      setBalance(balanceInfo);
      addTestResult('Get Balance', 'Success', `Total balance: ${balanceInfo?.total || 0} points`);
    } catch (error) {
      addTestResult('Get Balance', 'Failed', error.message);
    } finally {
      setLoading(false);
    }
  };

  // Test 5: Test webhook endpoint
  const testWebhook = async () => {
    setLoading(true);
    try {
      const webhookUrl = process.env.REACT_APP_USE_LOCAL_EMULATORS === 'true'
        ? 'http://localhost:5001/europe-fa766/us-central1/api/webhook/syrve-loyalty'
        : `${API_BASE}/webhook/syrve-loyalty`;

      const testPayload = {
        eventType: 'balance_changed',
        customerId: customerInfo?.id || 'test-customer-id',
        balance: 100,
        cardNumber: '1234567890',
        timestamp: new Date().toISOString()
      };

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(testPayload)
      });

      if (response.ok) {
        addTestResult('Webhook Test', 'Success', 'Webhook endpoint responded correctly');
      } else {
        addTestResult('Webhook Test', 'Failed', `Status: ${response.status}`);
      }
    } catch (error) {
      addTestResult('Webhook Test', 'Failed', error.message);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to add test results
  const addTestResult = (test, status, message) => {
    setTestResults(prev => [...prev, {
      test,
      status,
      message,
      timestamp: new Date().toLocaleTimeString()
    }]);
  };

  // Run all tests
  const runAllTests = async () => {
    setTestResults([]);
    await testConnection();
    if (user) {
      await testUserSync();
      await testGetPrograms();
      await testGetBalance();
    }
    await testWebhook();
  };

  return (
    <div className="syrve-test-container">
      <h1>Syrve API Integration Test</h1>

      <div className="connection-status">
        <h3>Connection Status: <span className={connectionStatus === 'Connected' ? 'success' : 'error'}>{connectionStatus}</span></h3>
        {user && <p>Logged in as: {user.email}</p>}
      </div>

      <div className="test-controls">
        <button onClick={testConnection} disabled={loading}>
          1. Test API Connection
        </button>
        <button onClick={testUserSync} disabled={loading || !user}>
          2. Sync User with Syrve
        </button>
        <button onClick={testGetPrograms} disabled={loading || connectionStatus !== 'Connected'}>
          3. Get Loyalty Programs
        </button>
        <button onClick={testGetBalance} disabled={loading || !user}>
          4. Get Wallet Balance
        </button>
        <button onClick={testWebhook} disabled={loading}>
          5. Test Webhook
        </button>
        <button onClick={runAllTests} disabled={loading} className="run-all">
          Run All Tests
        </button>
      </div>

      {loading && <div className="loading">Running test...</div>}

      {/* Organizations */}
      {organizations.length > 0 && (
        <div className="info-section">
          <h3>Organizations</h3>
          <select onChange={(e) => {
            const org = organizations.find(o => o.id === e.target.value);
            setSelectedOrg(org);
            getSyrveClient().setOrganization(org.id);
          }}>
            {organizations.map(org => (
              <option key={org.id} value={org.id}>
                {org.name} {org.isDisabled && '(Disabled)'}
              </option>
            ))}
          </select>
          {selectedOrg && (
            <div className="details">
              <p>ID: {selectedOrg.id}</p>
              <p>Address: {selectedOrg.restaurantAddress || 'N/A'}</p>
              <p>Timezone: {selectedOrg.timeZone || 'N/A'}</p>
            </div>
          )}
        </div>
      )}

      {/* Customer Info */}
      {customerInfo && (
        <div className="info-section">
          <h3>Customer Information</h3>
          <div className="details">
            <p>ID: {customerInfo.id}</p>
            <p>Name: {customerInfo.name} {customerInfo.surname}</p>
            <p>Phone: {customerInfo.phone}</p>
            <p>Email: {customerInfo.email}</p>
            <p>Cards: {customerInfo.cards?.length || 0}</p>
          </div>
        </div>
      )}

      {/* Balance */}
      {balance && (
        <div className="info-section">
          <h3>Wallet Balance</h3>
          <div className="details">
            <p>Total Points: {balance.total}</p>
            {balance.wallets?.map((wallet, index) => (
              <div key={index}>
                <p>Wallet {index + 1}: {wallet.name} - {wallet.balance} points (Type: {wallet.type})</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Programs */}
      {programs.length > 0 && (
        <div className="info-section">
          <h3>Loyalty Programs</h3>
          {programs.map((program, index) => (
            <div key={index} className="details">
              <p>Program: {program.name}</p>
              <p>Type: {program.type}</p>
            </div>
          ))}
        </div>
      )}

      {/* Test Results */}
      {testResults.length > 0 && (
        <div className="test-results">
          <h3>Test Results</h3>
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Test</th>
                <th>Status</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {testResults.map((result, index) => (
                <tr key={index} className={result.status === 'Success' ? 'success' : 'error'}>
                  <td>{result.timestamp}</td>
                  <td>{result.test}</td>
                  <td>{result.status}</td>
                  <td>{result.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="webhook-info">
        <h3>Webhook Configuration</h3>
        <p>Configure Syrve to send loyalty webhooks to:</p>
        <code>
          {process.env.REACT_APP_USE_LOCAL_EMULATORS === 'true'
            ? 'http://localhost:5001/europe-fa766/us-central1/api/webhook/syrve-loyalty'
            : `${API_BASE}/webhook/syrve-loyalty`}
        </code>
      </div>
    </div>
  );
};

export default SyrveTestPage;